import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useUserLocale } from '@src/hooks/useUserLocale';
import { trackClickEvent } from 'pe-core-frontend';
import { createContext, useContext, useEffect, useState } from 'react';
import { LANGUAGE_OPTIONS, PERSIST_LANGUAGE_PREFERENCE_URL } from './constants';
import { LanguageIframe } from './LanguageIframe';
export const LanguagePreferenceContext = createContext({
    currentLanguage: LANGUAGE_OPTIONS[0],
    handleLanguageChange: () => {
        console.warn('handleLanguageChange not implemented');
    },
    languagePreferenceClickable: () => {
        console.warn('languagePreferenceClickable not implemented');
    },
    setCurrentLanguage: () => {
        console.warn('setCurrentLanguage not implemented');
    },
    languageOptions: [],
});
/**
 * Custom hook to access language preference context. Must be used within a LanguagePreferenceProvider.
 */
export const useLanguagePreference = () => useContext(LanguagePreferenceContext);
/**
 * Sends language preference to cross-domain iframe for persistence across Guild domains.
 */
const sendLanguagePreference = (language) => {
    const data = {
        languagePreference: language,
        event: 'update',
    };
    const iframeEl = document.getElementById('language-preference-storage');
    if (!iframeEl || !iframeEl.contentWindow) {
        console.warn('Language preference iframe not found');
        return;
    }
    iframeEl.contentWindow.postMessage(JSON.stringify(data), PERSIST_LANGUAGE_PREFERENCE_URL);
};
/**
 * Provider component that manages language preference state and integrates with Weglot.
 * Handles language switching, analytics tracking, and cross-domain preference persistence.
 */
export const LanguagePreferenceProvider = ({ segmentAnalytics, children, productName, user, }) => {
    const [currentLanguage, setCurrentLanguage] = useState(LANGUAGE_OPTIONS[0]);
    const { setLocale } = useUserLocale(!user?.id);
    useEffect(() => {
        // Check if Weglot is properly initialized and available
        if (window.Weglot?.initialized && typeof window.Weglot.getCurrentLang === 'function') {
            const currentLanguageCode = window.Weglot.getCurrentLang();
            const currentLanguageOption = LANGUAGE_OPTIONS.find(option => option.value === currentLanguageCode);
            if (currentLanguageOption) {
                setCurrentLanguage(currentLanguageOption);
            }
            else {
                // If no current language is set, fall back to default language (first option - English)
                setCurrentLanguage(LANGUAGE_OPTIONS[0]);
            }
        }
        else {
            // If Weglot is not initialized yet, use the default language (first option - English)
            setCurrentLanguage(LANGUAGE_OPTIONS[0]);
        }
    }, []);
    // Listen for automatic language switches from WeglotWrapper
    useEffect(() => {
        const handleWeglotLanguageSwitched = (event) => {
            const languageCode = event.detail?.language;
            if (languageCode) {
                const languageOption = LANGUAGE_OPTIONS.find(option => option.value === languageCode);
                if (languageOption) {
                    setCurrentLanguage(languageOption);
                }
            }
        };
        window.addEventListener('weglotLanguageSwitched', handleWeglotLanguageSwitched);
        return () => {
            window.removeEventListener('weglotLanguageSwitched', handleWeglotLanguageSwitched);
        };
    }, []);
    /**
     * Tracks click events using Segment analytics or default tracking.
     */
    const languagePreferenceClickable = (text, tag = 'language_preference', type = 'button') => {
        if (segmentAnalytics) {
            segmentAnalytics.track('Clickable', {
                productName,
                loggedIn: true,
                tag,
                text,
                type,
            });
        }
        else {
            trackClickEvent(text, tag, type, '');
        }
    };
    /**
     * Handles language change by updating Weglot, tracking analytics, and persisting preferences.
     */
    const handleLanguageChange = async (languageCode) => {
        // Check if Weglot is properly initialized and available
        if (!window.Weglot?.initialized || typeof window.Weglot.switchTo !== 'function') {
            console.warn('Weglot not properly initialized for language switching');
            return;
        }
        const languageOption = LANGUAGE_OPTIONS.find(option => option.value === languageCode);
        window.Weglot.switchTo(languageCode);
        const languageChangeEvent = new CustomEvent('languageChangeInitiated', {
            detail: { language: languageCode },
        });
        window.dispatchEvent(languageChangeEvent);
        if (languageOption) {
            languagePreferenceClickable(languageOption?.name, 'set_language_preference');
            setCurrentLanguage(languageOption);
            sendLanguagePreference(languageOption.value);
            if (user?.id) {
                setLocale(languageOption.locale);
            }
        }
    };
    const languageOptions = LANGUAGE_OPTIONS;
    return (_jsxs(LanguagePreferenceContext.Provider, { value: {
            currentLanguage,
            handleLanguageChange,
            languageOptions,
            languagePreferenceClickable,
            setCurrentLanguage,
        }, children: [_jsx(LanguageIframe, {}), children] }));
};
